import { useState, useMemo } from 'react';
import { useFormik } from 'formik';
import { RedoOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import ConfirmationModal from 'components/new-ui/composed/modal/confirmation-modal/ConfirmationModal';
import { Col, List, Row, Space, Typography } from 'antd';
import FormSelect, { SelectOption } from 'components/new-ui/composed/formSelect/FormSelect';
import FormInput from 'components/new-ui/composed/formInput/FormInput';
import { getErrorMessage } from 'utils/getErrorMessage';
import Notification from 'components/ui/base/notification/Notification';
import DeleteExternalReferenceModal from './DeleteExternalReferenceModal';
import IconButton from 'components/new-ui/base/icon-button/IconButton';
import { ExternalReference, ExternalReferencesModalType, referenceTypeOptionsMap } from 'utils/external-references';

type Props = {
    open: boolean;
    type: ExternalReferencesModalType;
    externalReferences: ExternalReference[];
    onClose: () => void;
    onUpdateExternalReferences: (ref: ExternalReference) => Promise<void>;
    onRemoveExternalReference: (ref: ExternalReference) => Promise<void>;
};

const ExternalReferencesModal = ({
    type,
    open,
    externalReferences,
    onClose,
    onRemoveExternalReference,
    onUpdateExternalReferences,
}: Props) => {
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const usedTypes = externalReferences.map((r) => r.referenceType);

    const referenceTypeOptions = useMemo(() => referenceTypeOptionsMap[type], [type]);

    const selectOptionsWithDisable: SelectOption[] = referenceTypeOptions.map((opt) => {
        const isUsed = usedTypes.includes(opt.id.toString());

        // When editing: allow selecting the current type
        const isEditingCurrent = editingIndex !== null && externalReferences[editingIndex]?.referenceType === opt.id;

        return {
            ...opt,
            disabled: editingIndex === null ? isUsed : !isEditingCurrent && isUsed,
        };
    });

    const allReferenceTypesAdded = useMemo(
        () => selectOptionsWithDisable.every((option) => option.disabled),
        [selectOptionsWithDisable]
    );

    const disabledFormFields: boolean = useMemo(
        () => editingIndex === null && allReferenceTypesAdded,
        [allReferenceTypesAdded, editingIndex]
    );

    const formik = useFormik<ExternalReference>({
        initialValues: { referenceType: '', referenceNumber: '' },
        enableReinitialize: false,
        onSubmit: async (values, helpers) => {
            try {
                await onUpdateExternalReferences(values);
            } catch (err) {
                const error = getErrorMessage(err);
                Notification({
                    type: 'error',
                    messageTitle: 'Error',
                    description: error || 'Cound not CREATE/UPDATE reference',
                    placement: 'top',
                });
            } finally {
                setEditingIndex(null);
                helpers.resetForm();
            }
        },
    });

    const handleEdit = (index: number) => {
        const ref = externalReferences[index];
        setEditingIndex(index);
        formik.setValues(ref);
    };

    const handleDelete = async (index: number) => {
        setShowDeleteModal(true);
        setEditingIndex(index);
    };

    const handleCancel = () => {
        setEditingIndex(null);
        formik.resetForm();
        onClose();
    };

    const isValid = !!formik.values.referenceType && !!formik.values.referenceNumber;

    return (
        <ConfirmationModal
            icon={<RedoOutlined />}
            open={open}
            title="Add External Reference"
            onConfirm={formik.submitForm}
            onCancel={handleCancel}
            confirmButtonText={editingIndex !== null ? 'Save' : 'Add'}
            confirmationButtonLoading={formik.isSubmitting}
            confirmationButtonDisabled={!isValid || formik.isSubmitting}
            confirmButtonProps={{
                form: 'externalReferenceForm',
                htmlType: 'submit',
            }}
            content={
                <>
                    <Space direction="vertical" size={24} style={{ width: '100%' }}>
                        <form id="externalReferenceForm" style={{ width: '100%' }} onSubmit={formik.handleSubmit}>
                            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                                <Typography>Please select reference type and enter your reference number</Typography>

                                <FormSelect
                                    disabled={disabledFormFields}
                                    hideKeys
                                    style={{ maxWidth: 375 }}
                                    label="Reference Type :"
                                    placeholder="Select Reference Type"
                                    fieldProps={formik.getFieldProps('referenceType')}
                                    fieldHelper={formik.getFieldHelpers('referenceType')}
                                    fieldMeta={formik.getFieldMeta('referenceType')}
                                    selectOptions={selectOptionsWithDisable}
                                />

                                <FormInput
                                    disabled={disabledFormFields}
                                    inputStyles={{ maxWidth: 375 }}
                                    label="Reference Number :"
                                    placeholder="Enter reference number"
                                    fieldProps={formik.getFieldProps('referenceNumber')}
                                    fieldMeta={formik.getFieldMeta('referenceNumber')}
                                    fieldHelper={formik.getFieldHelpers('referenceNumber')}
                                />
                            </Space>
                        </form>

                        {externalReferences?.length > 0 && (
                            <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                <Typography.Text strong>External References</Typography.Text>
                                <List
                                    dataSource={externalReferences}
                                    renderItem={(item, index) => (
                                        <List.Item>
                                            <Row
                                                gutter={[16, 8]}
                                                justify="space-between"
                                                align="middle"
                                                style={{ width: '100%' }}
                                            >
                                                <Col span={6}>
                                                    <Typography.Text>
                                                        {referenceTypeOptions.find(
                                                            (opt) => opt.id === item.referenceType
                                                        )?.value || item.referenceType}
                                                    </Typography.Text>
                                                </Col>
                                                <Col span={14}>
                                                    <Typography.Text>{item.referenceNumber}</Typography.Text>
                                                </Col>
                                                <Col
                                                    span={4}
                                                    style={{
                                                        display: 'flex',
                                                        justifyContent: 'flex-end',
                                                        gap: 8,
                                                    }}
                                                >
                                                    <IconButton
                                                        tooltipText="Edit"
                                                        icon={
                                                            <EditOutlined
                                                                style={{ fontSize: 16 }}
                                                                onClick={() => handleEdit(index)}
                                                            />
                                                        }
                                                    />
                                                    <IconButton
                                                        tooltipText="Delete"
                                                        icon={
                                                            <DeleteOutlined
                                                                style={{ fontSize: 16 }}
                                                                onClick={() => handleDelete(index)}
                                                            />
                                                        }
                                                    />
                                                </Col>
                                            </Row>
                                        </List.Item>
                                    )}
                                />
                            </Space>
                        )}
                    </Space>

                    <DeleteExternalReferenceModal
                        referenceOrigin={type}
                        open={showDeleteModal}
                        onCancel={() => {
                            setEditingIndex(null);
                            setShowDeleteModal(false);
                        }}
                        onDelete={async () => {
                            if (editingIndex === null || !externalReferences[editingIndex]) return;

                            const ref = externalReferences[editingIndex];
                            if (!ref) return;

                            await onRemoveExternalReference(ref);
                            setEditingIndex(null);
                            setShowDeleteModal(false);
                        }}
                    />
                </>
            }
        />
    );
};

export default ExternalReferencesModal;
